import { useState, type ReactNode } from "react";
import { ChevronRight, Copy } from "lucide-react";
import { Button, Card, Chip } from "@/components/ds";
import { OrganizationBrandingCard } from "@/components/corporate/organizations/OrganizationBrandingCard";
import { appDisplayName, countryLabel, languageLabel } from "@/components/corporate/organizations/orgOptions";
import { DEFAULT_JOIN_LINK_PRESET, joinLinkRange } from "@/components/join-links/joinLinkRange";
import { linkLabel } from "@/components/join-links/joinLinkFormat";
import { activeSubscription, useOrgSubscriptions } from "@/hooks/useOrgSubscriptions";
import { useJoinLinks } from "@/hooks/useJoinLinks";
import { useEmployees } from "@/hooks/useEmployees";
import { useDepartments } from "@/hooks/useDepartments";
import { useLocations } from "@/hooks/useLocations";
import { useOrgAccessSettings } from "@/hooks/useOrgAccessSettings";
import { useOrgFeedSettings } from "@/hooks/useOrgFeedSettings";
import { useOrgReplyDisplayName } from "@/hooks/useOrgReplyDisplayName";
import { useOrgEntraSyncStatus } from "@/hooks/useOrgEntraSyncStatus";
import { useOrgAllowedDomains } from "@/hooks/useOrgAllowedDomains";
import { formatDubaiDate } from "@/lib/dubaiDate";
import type { Organization } from "@/types/organizations";

interface OrgOverviewTabProps {
  organization: Organization;
}

function OverviewCard({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <Card className="px-4 py-3.5" data-testid={testId}>
      <h2 className="mb-2 text-[14px] font-semibold text-ds-ink">{title}</h2>
      {children}
    </Card>
  );
}

/** One value that opens where it is changed (`#settings-<section>`, `#integrations`, `#people`). */
function LinkRow({ label, value, href }: { label: string; value: ReactNode; href: string }) {
  return (
    <a
      href={href}
      className="-mx-2 flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-[13.5px] hover:bg-ds-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent"
    >
      <span className="text-ds-ink3">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5 text-right font-medium text-ds-ink">
        <span className="min-w-0 truncate">{value}</span>
        <ChevronRight className="h-3.5 w-3.5 flex-none text-ds-ink3" aria-hidden="true" />
      </span>
    </a>
  );
}

function PlainRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-[13.5px]">
      <span className="text-ds-ink3">{label}</span>
      <span className="min-w-0 truncate text-right font-medium text-ds-ink">{value}</span>
    </div>
  );
}

const onOff = (value: boolean | undefined, loading: boolean) =>
  loading || value === undefined ? (
    <span className="text-ds-ink3">–</span>
  ) : value ? (
    <Chip tone="ok">On</Chip>
  ) : (
    <Chip tone="neutral">Off</Chip>
  );

const count = (value: number | undefined, loading: boolean) => (loading || value === undefined ? "–" : value.toLocaleString("en-US"));

/**
 * Overview — "At a glance", built only from existing reads: counts, plan & seats (the active
 * subscription), the join link (the Join links list's row for this company), what's switched on,
 * sign-in and branding. Every value links to where it is changed; nothing is shown twice.
 */
export function OrgOverviewTab({ organization }: OrgOverviewTabProps) {
  const id = organization.id;
  const employees = useEmployees(id);
  const departments = useDepartments(id);
  const locations = useLocations(id);
  const subscriptions = useOrgSubscriptions(id);
  const joinLinks = useJoinLinks(joinLinkRange(DEFAULT_JOIN_LINK_PRESET));
  const access = useOrgAccessSettings(id);
  const feed = useOrgFeedSettings(id);
  const replyName = useOrgReplyDisplayName(id);
  const entra = useOrgEntraSyncStatus(id);
  const domains = useOrgAllowedDomains(id);
  const [copied, setCopied] = useState(false);

  const plan = activeSubscription(subscriptions.data);
  const joinRow = (joinLinks.data ?? []).find((row) => row.organizationId === id);
  const joinUrl = joinRow && joinRow.available !== false && joinRow.joinUrl ? joinRow.joinUrl : null;

  const signInMethod = entra.isLoading
    ? "Checking…"
    : entra.isError || !entra.data
      ? "Unknown"
      : entra.data.isEnabled
        ? "Microsoft sign-in"
        : "Email code";

  const copyJoinLink = async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="org-overview">
      <OverviewCard title="At a glance">
        <LinkRow label="Employees" value={count(employees.employees?.length, employees.isLoading)} href="#people" />
        <LinkRow label="Departments" value={count(departments.departments?.length, departments.isLoading)} href="#people" />
        <LinkRow label="Locations" value={count(locations.locations?.length, locations.isLoading)} href="#people" />
      </OverviewCard>

      <OverviewCard title="Plan & seats" testId="overview-plan">
        {subscriptions.isLoading ? (
          <p className="text-[13px] text-ds-ink3">Loading…</p>
        ) : subscriptions.isError ? (
          <p className="text-[13px] text-ds-err">Couldn&apos;t load the plan.</p>
        ) : !plan ? (
          <p className="text-[13.5px] text-ds-ink2">No active plan</p>
        ) : (
          <>
            <PlainRow label="Status" value={<Chip tone={plan.status === "Active" ? "ok" : "neutral"}>{plan.status ?? "Active"}</Chip>} />
            <PlainRow label="Seats" value={`${plan.allocatedSeats ?? 0} of ${plan.purchasedSeats ?? 0} used`} />
            {plan.billingCycle && plan.billingCycle !== "None" && <PlainRow label="Billing" value={plan.billingCycle} />}
            {plan.nextBillingDate && <PlainRow label="Next billing" value={formatDubaiDate(plan.nextBillingDate)} />}
          </>
        )}
      </OverviewCard>

      <OverviewCard title="Join link">
        <p className="mb-2 text-[12.5px] text-ds-ink3">How employees join — the same link as the Join links page.</p>
        <div className="font-ds-mono text-[12.5px] text-ds-ink" data-testid="overview-join-link">
          {joinLinks.isLoading ? "–" : joinUrl ? linkLabel(joinUrl) : <span className="font-ds text-ds-ink2">No join link yet</span>}
        </div>
        {joinUrl && (
          <div className="mt-3">
            <Button size="sm" variant="secondary" icon={<Copy className="h-3.5 w-3.5" aria-hidden="true" />} onClick={() => void copyJoinLink()}>
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        )}
      </OverviewCard>

      <OverviewCard title="Company">
        <LinkRow label="Country" value={countryLabel(organization.country) || "Not set"} href="#settings-details" />
        <LinkRow label="Language" value={languageLabel(organization.defaultLanguage) || "Not set"} href="#settings-language" />
        <LinkRow label="App" value={appDisplayName(organization.appScheme)} href="#settings-app" />
        <PlainRow label="Created" value={formatDubaiDate(organization.createdAt)} />
        <PlainRow label="Company ID" value={<span className="font-ds-mono text-[12px]">{organization.id}</span>} />
      </OverviewCard>

      <OverviewCard title="Switched on">
        <LinkRow label="Social feed" value={onOff(feed.data?.masterFeedEnabled, feed.isLoading)} href="#settings-features" />
        <LinkRow label="Chat" value={onOff(access.data ? access.data.chatEnabled ?? true : undefined, access.isLoading)} href="#settings-features" />
        <LinkRow label="Perks" value={onOff(access.data ? access.data.perksEnabled ?? true : undefined, access.isLoading)} href="#settings-features" />
        <LinkRow
          label="Health onboarding"
          value={onOff(access.data ? access.data.healthOnboardingEnabled ?? true : undefined, access.isLoading)}
          href="#settings-features"
        />
        <LinkRow
          label="Guest users"
          value={onOff(access.data ? access.data.allowExternalUsers ?? true : undefined, access.isLoading)}
          href="#settings-features"
        />
        <LinkRow
          label="Reply name"
          value={replyName.isLoading ? "–" : replyName.data?.replyDisplayName?.trim() || "Company name"}
          href="#settings-features"
        />
      </OverviewCard>

      <OverviewCard title="Sign-in">
        <LinkRow label="Sign-in method" value={signInMethod} href="#integrations" />
        <LinkRow label="Allowed email domains" value={count(domains.data?.length, domains.isLoading)} href="#integrations" />
      </OverviewCard>

      <OrganizationBrandingCard
        organization={organization}
        action={
          <a
            href="#settings-branding"
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-ds-accent-ink hover:underline"
          >
            Edit branding
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        }
      />
    </div>
  );
}

export default OrgOverviewTab;
